import { API, ApiArgs } from '@/api/API'
import APIService from '@/api/DeprecatedAPIService'
import type { ApplyCodePayload } from '@/api/types/payloads'
import { QueryParams } from '@/helpers/QueryStringParameters'

export const fetchCartEmbeds =
  'identity,ticket,ticket.ticket_type,ticket.ticket_group,ticket_group.meta,ticket.event_session,ticket.event_template,event_template.venue,event_template.meta'
export type CartApiResponse = ApiResponse<
  | 'cart'
  | 'cart_fees'
  | 'cartmod'
  | 'event_session'
  | 'event_template'
  | 'identity'
  | 'meta'
  | 'ticket'
  | 'ticket_group'
  | 'ticket_type'
  | 'venue'
>

export function fetchOwnCart(id: string): Promise<CartApiResponse> {
  return API.getUncached(`my/cart/${id}`, fetchCartArgs())
}

export function fetchCart(id: string, query?: QueryParams): Promise<CartApiResponse> {
  return API.getUncached(`cart/${id}`, {
    ...fetchCartArgs(),
    query,
  })
}

export function fetchCartArgs(): ApiArgs {
  return {
    embed: fetchCartEmbeds,
    limit: 1000,
  }
}

export function applyCartCodes(id: string, payload: ApplyCodePayload) {
  return API.post(`my/cart/${id}/apply_code`, {
    body: payload,
    embed: fetchCartEmbeds,
    limit: 1000,
  })
}

/**
 * @deprecated Use reserveTickets()
 */
export function reserveTicketsWithoutUpdatingCartStore(cartId, ticketTypesRequired, additionalInfo?) {
  const payload: Dict<any> = {
    tickets: ticketTypesRequired,
  }

  // Include additional info property only if not an empty array
  if (additionalInfo && additionalInfo.length > 0) {
    payload.additional_info = additionalInfo
  }

  return APIService.post(`my/cart/${cartId}/reserve`, payload)
}

/**
 * This does not appear to ever be called with no parameter.
 * @deprecated Use loggedInPrefix().
 */
export function getCartApiPrefix(id) {
  return id === undefined ? '' : 'my/'
}

export function generateSimilarReservePayload(upsellRequests, eventSession) {
  const ticketTypesRequired: any[] = []

  upsellRequests.forEach((upsellRequest) => {
    ticketTypesRequired.push({
      ticket_type_id: upsellRequest.ticket_type_similar.id,
      event_session_id: eventSession.id,
      admit_name: upsellRequest.name,
      admit_email: '',
    })
  })

  return ticketTypesRequired
}

/**
 * @param ticketGroups All ticket groups of both the ticket type pool and the
 *                     master ticket type
 * @param ticketTypes Pool of ticket types to choose from
 * @param ticketGroup Ticket group owner of ticket type below
 * @param ticketType Ticket type to use name of
 *
 * @deprecated Only MSI/GRPM use this.
 */
export function getSimilarTicketType(ticketGroups, ticketTypes, ticketGroup, ticketType) {
  // We need to get a similar ticket type from the upsell event's ticket
  // types, but we also need to ensure it belongs a similar ticket group. For
  // example, if the master ticket belongs to "Public" ticket group, then get
  // a upsell ticket type from the "Public" ticket group, or "Members" ticket
  // group, etc.
  let upsellTicketGroup

  // Find all upsell ticket groups that match the hidden_type of the master ticket group
  const upsellTicketGroupMatches = ticketGroups.filter((val) => val.hidden_type === ticketGroup.hidden_type)

  if (upsellTicketGroupMatches.length === 1) {
    // If only one result, use that
    upsellTicketGroup = upsellTicketGroupMatches[0]
  } else {
    // Otherwise attempt to match against ticket group names
    upsellTicketGroup = ticketGroups.find((val) => val.name === ticketGroup.name)
  }

  // If still no match, return null. The calling function must be able to
  // handle the possibility that this admission ticket type has no
  // corresponding upsell ticket type.
  if (!upsellTicketGroup) {
    return null
  }

  // Match ticket types against their names
  return ticketTypes.find((val) => val.name === ticketType.name && val.ticket_group_id === upsellTicketGroup.id)
}
